import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Data,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationEnd,
  NavigationStart,
  Params,
  ResolveEnd,
  ResolveStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  Scroll
} from '@angular/router';
import { slashJoin, withLeadingSlash } from 'path-slashes';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  getDeepestChildRoute,
  getRoutePathParams,
  getRoutePathSegments
} from 'src/app/core/services/activated-route/activated-route.utils';

@Injectable({
  providedIn: 'root'
})
export class RouterEventsService {
  public constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  public get loadStart$(): Observable<RouteConfigLoadStart> {
    return this.router.events.pipe(filter(event => event instanceof RouteConfigLoadStart));
  }

  public get loadEnd$(): Observable<RouteConfigLoadEnd> {
    return this.router.events.pipe(filter(event => event instanceof RouteConfigLoadEnd));
  }

  public get guardsCheckStart$(): Observable<GuardsCheckStart> {
    return this.router.events.pipe(filter(event => event instanceof GuardsCheckStart));
  }

  public get guardsCheckEnd$(): Observable<GuardsCheckEnd> {
    return this.router.events.pipe(filter(event => event instanceof GuardsCheckEnd));
  }

  public get resolveStart$(): Observable<ResolveStart> {
    return this.router.events.pipe(filter(event => event instanceof ResolveStart));
  }

  public get resolveEnd$(): Observable<ResolveEnd> {
    return this.router.events.pipe(filter(event => event instanceof ResolveEnd));
  }

  public get scroll$(): Observable<Scroll> {
    return this.router.events.pipe(filter(event => event instanceof Scroll));
  }

  public get navigationStart$(): Observable<NavigationStart> {
    return this.router.events.pipe(filter(event => event instanceof NavigationStart));
  }

  public get navigationEnd$(): Observable<NavigationEnd> {
    return this.router.events.pipe(filter(event => event instanceof NavigationEnd));
  }

  public get navigationEndRoute$(): Observable<ActivatedRouteSnapshot> {
    return this.navigationEnd$.pipe(
      filter(() => this.activatedRoute.outlet === 'primary'),
      map(() => getDeepestChildRoute(this.activatedRoute.snapshot))
    );
  }

  public get navigationEndRouteData$(): Observable<Data> {
    return this.navigationEndRoute$.pipe(map(route => route.data));
  }

  public get navigationEndRoutePathSegments$(): Observable<string[]> {
    return this.navigationEndRoute$.pipe(map(route => getRoutePathSegments(route)));
  }

  public get navigationEndRoutePath$(): Observable<string> {
    return this.navigationEndRoutePathSegments$.pipe(map(segments => withLeadingSlash(slashJoin(segments))));
  }

  public get navigationEndRoutePathParams$(): Observable<Params> {
    return this.navigationEndRoute$.pipe(map(route => getRoutePathParams(route)));
  }
}
